import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IFilter, FILTER } from './filterTypes';
import { omit } from 'lodash';

 interface IFilterState {
    filters: IFilter[]; // List of filters
    selectedFilter: IFilter | null; // Single filter (e.g., from getFilterById)
    isBusy: boolean; // 
    isFetched: boolean; // Flag for state is fetched or not
    isFiltersLoading: boolean;
    error?: string;
}

const initialState: IFilterState = {
    filters: [],
    selectedFilter: null,
    isBusy: false,
    isFetched: false,
    isFiltersLoading: false,
    error: undefined,
};

const filterSlice = createSlice({
    name: FILTER,
    initialState,
    reducers: {
        addDraftFilterRequest: (state, action: PayloadAction<{ filter: IFilter }>) => {
            state.filters = [action.payload.filter].concat(state.filters);
            state.selectedFilter = null;
        },
        deleteDraftFilterRequest: (state, action: PayloadAction<{ tempId: string }>) => {
            state.filters = state.filters.filter((p) => p.tempId !== action.payload.tempId);
            state.selectedFilter = null;
        },
        updateDraftFilterRequest: (state, action: PayloadAction<{ filter: IFilter }>) => {
            // Not saved draft can be updated or already saved filter can be updated
            if (action.payload.filter.id === null && action.payload.filter.tempId && action.payload.filter.isDraft === true) {
                state.filters = state.filters.map((p) =>
                    p.tempId === action.payload.filter.tempId ? { ...p, ...action.payload.filter } : p
                );
            }
            else {
                state.filters = state.filters.map((p) =>
                    p.id === action.payload.filter.id ? { ...p, ...action.payload.filter } : p
                );
            }
            state.selectedFilter = null;
        },
        addFilterRequest: (state, _action: PayloadAction<{ filter: IFilter }>) => {
            state.isBusy = false;
        },
        addFilterSuccess: (state, action: PayloadAction<{ filter: IFilter }>) => {
            state.isBusy = false;
            if (action.payload.filter.tempId) {
                state.filters = state.filters.filter((p) => p.tempId !== action.payload.filter.tempId);
            }
            state.filters.push(omit(action.payload.filter, 'tempId'));
            state.selectedFilter = omit(action.payload.filter, 'tempId');
        },
        updateFilterRequest: (state, _action: PayloadAction<{ filter: IFilter }>) => {
            state.isBusy = true;
        },
        updateFilterSuccess: (state, action: PayloadAction<{ filter: IFilter }>) => {
            state.isBusy = false;
            state.filters = state.filters.map((p) =>
                p.id === action.payload.filter.id ? action.payload.filter : p
            );
            state.selectedFilter = action.payload.filter;
        },
        deleteFilterRequest: (state, _action: PayloadAction<{ id: number }>) => {
            state.isBusy = true;
        },
        deleteFilterSuccess: (state, action: PayloadAction<{ deletedId: number }>) => {
            state.isBusy = false;
            state.filters = state.filters.filter((p) => p.id !== action.payload.deletedId);
            state.selectedFilter = null;
        },
        getFilterRequest: (state, _action: PayloadAction<{ id: number }>) => {
            state.isBusy = true;
        },
        getFilterSuccess: (state, action: PayloadAction<{ filter: IFilter }>) => {
            state.isBusy = false;
            state.filters = state.filters.map((p) =>
                p.id === action.payload.filter.id ? action.payload.filter : p
            );
            state.selectedFilter = action.payload.filter;
        },
        getFiltersRequest: (state) => {
            state.isFiltersLoading = true;
            state.isFetched = false;
        },
        getFiltersSuccess(state, action: PayloadAction<{ filters: IFilter[] }>) {
            state.isFiltersLoading = false;
            state.filters = action.payload.filters;
            state.isFetched = true;
        },
        setSelectedFilterRequest: (state, action: PayloadAction<{ id: number | null, tempId?: string, isDraft?: boolean }>) => {
            state.isFiltersLoading = false;
            if (action.payload.id === null && action.payload.tempId && action.payload.isDraft === true) {
                state.selectedFilter = state.filters.find((p) => p.tempId === action.payload.tempId) ?? null;
            }
            else if(action.payload.id !== null) {
                state.selectedFilter = state.filters.find((p) => p.id === action.payload.id) ?? null;
            }
            else {
                state.selectedFilter = null;
            }
        },
        setFilterError: (state, action: PayloadAction<{ message: string }>) => {
            state.isFiltersLoading = false;
            state.isBusy = false;
            state.error = action.payload.message;
        },
        displayOutputByFilterIdRequest: (state, _action: PayloadAction<{ id: number | string }>) => {
            state.isBusy = true;
        },
        // displayOutputByFilterImoListRequest: (state, _action: PayloadAction<{ imoList: number[], name: string }>) => {
        //     state.isBusy = true;
        // },
        displayOutputByFilterSuccess: (state) => {
            state.isBusy = false;
            state.selectedFilter = null;
        },
    },
});

export const {
    addDraftFilterRequest,
    deleteDraftFilterRequest,
    updateDraftFilterRequest,
    addFilterRequest,
    addFilterSuccess,
    updateFilterRequest,
    updateFilterSuccess,
    deleteFilterRequest,
    deleteFilterSuccess,
    getFilterRequest,
    getFilterSuccess,
    getFiltersRequest,
    getFiltersSuccess,
    setSelectedFilterRequest,
    setFilterError,
    displayOutputByFilterIdRequest,
    // displayOutputByFilterImoListRequest,
    displayOutputByFilterSuccess,
} = filterSlice.actions;

export default filterSlice.reducer;

